import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { LocaleResolverService } from 'services/locale/locale-resolver';
import { SiteLanguageLabel, SiteNameSpace } from 'constants/locale';
import type { TenantId } from 'tenants/types/tenant';
import { getTenantById } from 'tenants/manager';

export function useLocaleSwitcher(tenantId: TenantId) {
  const { i18n, t, ready } = useTranslation(SiteNameSpace.Common);
  const router = useRouter();
  const tenant = getTenantById(tenantId);

  const localeService = useMemo(
    () => new LocaleResolverService(tenantId, router.locale),
    [tenantId, router.locale]
  );

  // Initialize with resolved locale
  useEffect(() => {
    if (ready) {
      const resolvedLocale = localeService.resolveLocale();
      if (resolvedLocale !== i18n.language) {
        i18n.changeLanguage(resolvedLocale);
      }
    }
  }, [ready, i18n, localeService]);

  const changeLocale = useCallback(async (code: string) => {
    await Promise.all([
      localeService.savePreference(code),
      i18n.changeLanguage(code),
    ]);
  }, [i18n, localeService]);

  const localeOptions = tenant.localization.languages.map(code => ({
    id: code,
    label: `${t(SiteLanguageLabel[code])} - ${code.toUpperCase()}`,
    isLink: false,
  }));

  return {
    isEnabled: localeService.isEnabled(),
    isReady: ready,
    currentLocale: i18n.language,
    localeOptions,
    changeLocale,
  };
} 